import { AlertBanner } from '@/components/alert-banner.js';
import {
  dataPurgeHardDeleteBlock,
  dataPurgeHardDeleteBlockCopy,
  type DataPurgeHardDeleteBlock,
} from '@airreading/feature-flags';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { ComponentProps } from 'react';

import { PurgeConfirmationGate } from './purge-confirmation-gate.js';

const HARD_DELETE_NOTE = 'This cannot be undone. Every row in the exports above is deleted outright, not archived.';

type PurgeHardDeleteConfirmationProps = Omit<ComponentProps<typeof PurgeConfirmationGate>, 'note' | 'disabled'> & {
  /** Extra disable, for example when a dry-run export failed. */
  disabled?: boolean;
};

/**
 * `useFlags` stays current after LaunchDarkly `identify` retargets the acting user.
 * `useFeatureFlag` reads `variation` once and would keep the anonymous value.
 */
function useDataPurgeAllowedFlag(): unknown {
  const flags = useFlags();
  return flags['data-purge-allowed'];
}

export const PurgeHardDeleteConfirmation = ({
  disabled = false,
  testId,
  ...gateProps
}: PurgeHardDeleteConfirmationProps) => {
  const block = dataPurgeHardDeleteBlock(useDataPurgeAllowedFlag());

  return (
    <>
      {block && <DataPurgeHardDeleteBanner block={block} testId={`${testId}-flag-banner`} />}
      <PurgeConfirmationGate
        {...gateProps}
        testId={testId}
        note={HARD_DELETE_NOTE}
        disabled={block !== null || disabled}
      />
    </>
  );
};

const DataPurgeHardDeleteBanner = ({ block, testId }: { block: DataPurgeHardDeleteBlock; testId: string }) => {
  const { title, message } = dataPurgeHardDeleteBlockCopy[block];

  return (
    <div data-testid={testId}>
      <AlertBanner title={title} content={message} />
    </div>
  );
};
